import React from "react";
import { DotLegalTextField } from "@dotlegal/dotlegal-ui-components";
import { useTranslation } from "@localization/useTranslation";
import { ValidationError } from "@common/validationError";
import TagsMultiSelect from "@common/components/tagsMultiSelect/TagsMultiSelect";
import { TagRelationType } from "@common/components/tagsMultiSelect/TagsMultiSelect.types";
import { EntityTemplateField, ProcessStandardField } from "../../entityTemplates/EntityTemplate.types";
import { ProcessCustomFieldRenderer, ProcessFieldModel, ProcessStandardFieldRenderer } from "../../entityTemplates/ProcessFieldRenderers";

export interface ProcessTemplateFieldProps {
    field: EntityTemplateField;
    name: string;
    onNameChange: (name: string) => void;
    nameErrorText?: string;
    process: ProcessFieldModel;
    onChange: (changeFunc: (process: ProcessFieldModel) => void) => Promise<void>;
    validationErrors: Array<ValidationError>;
    selectedTagIds: Array<string>;
    onTagsChange: (tagIds: Array<string>) => void;
}

/**
 * One template field, rendered for a process that has not been created yet. Every branch here replaces a standard
 * renderer that either saves on its own or needs a real process id. The rest go through the standard renderers
 * untouched.
 */
function ProcessTemplateField(props: ProcessTemplateFieldProps) {
    const { translateString } = useTranslation();
    const standardField = props.field.standardFieldIdentifier as ProcessStandardField | undefined;

    // The standard name field debounces into the process; the pane keeps the name on its save model so it can be
    // validated before anything else
    if (standardField === ProcessStandardField.ProcessName) {
        return (
            <DotLegalTextField
                debounce={false}
                size="small"
                label={translateString("name")}
                value={props.name}
                onChange={props.onNameChange}
                errorText={props.nameErrorText}
                autoFocus
            />
        );
    }

    if (standardField === ProcessStandardField.ProcessTags) {
        return (
            <TagsMultiSelect
                selectedTags={props.selectedTagIds}
                onChange={props.onTagsChange}
                entityType={TagRelationType.Process}
                isLoading={false}
                errorText={props.validationErrors.find((v) => v.field === ProcessStandardField[ProcessStandardField.ProcessTags])?.error}
            />
        );
    }

    if (standardField) {
        return (
            <ProcessStandardFieldRenderer
                field={props.field}
                process={props.process}
                processId=""
                onChange={props.onChange}
                validationErrors={props.validationErrors}
                isLoading={false}
                isPreview={false}
                disableAccessWarning
            />
        );
    }

    return (
        <ProcessCustomFieldRenderer
            field={props.field}
            process={props.process}
            processId=""
            onChange={props.onChange}
            validationErrors={props.validationErrors}
            isLoading={false}
            isPreview={false}
        />
    );
}

export default ProcessTemplateField;
